import {
  ArrowRightIcon,
  CalendarIcon,
  ChartLineIcon,
  MailIcon,
  MinusIcon,
  PercentIcon,
  PinIcon,
  PlusIcon,
  SendIcon,
  TagsIcon,
  Trash2Icon,
  Undo2Icon,
  UploadIcon,
  UserPlusIcon,
  UserXIcon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";

export type IdeaProjectEventAction =
  | OpenAPI.Schemas["IdeaProjectEvent"]["action"]
  | "assessorAssigned"
  | "assessorUnassigned"
  | "assessorInvited"
  | "ideaSubmitted"
  | "ideaUnsubmitted"
  | "labelsAdded"
  | "labelsRemoved"
  | "isPinnedUpdated"
  | "changedSubmissionDeadline"
  | "sentAuthorEmail"
  | "sentAssessmentFeedback"
  | "impactStatementCreated"
  | "impactStatementDeleted"
  | "finalOutcomeSubmitted";

export type IProjectIdeaEvent = Omit<
  OpenAPI.Schemas["IdeaProjectEvent"],
  "action"
> & {
  action: IdeaProjectEventAction;
  previousLane?: string;
  userObject?: OpenAPI.Schemas["User"];
  targetUserObject?: OpenAPI.Schemas["User"];
  targetLaneObject?: OpenAPI.Schemas["ProjectLane"];
  previousLaneObject?: OpenAPI.Schemas["ProjectLane"];
  targetIdea?: OpenAPI.Schemas["Idea"];
  automationObject?: { _id: string; name?: string };
  interacted?: string[];
  invitee?: string;
  labels?: string[];
  isPinned?: boolean;
  stage?: string;
  notifyAuthors?: boolean;
};

export const hasEventType = (
  action: IdeaProjectEventAction,
): action is keyof typeof eventTypes => action in eventTypes;

export const getLaneNameFromEvent = (
  event: IProjectIdeaEvent,
  defaultLaneName: string,
  laneKey: "previousLane" | "targetLane" = "previousLane",
) => {
  const laneId =
    laneKey === "previousLane" ? event.previousLane : event.targetLane;
  const laneObject =
    laneKey === "previousLane"
      ? event.previousLaneObject
      : event.targetLaneObject;
  if (!laneId) {
    return defaultLaneName;
  }
  if (laneObject) {
    return laneObject.name;
  }
  return "an unknown lane";
};

export const eventTypes = {
  addedToBoard: { icon: PlusIcon, color: "green" },
  removedFromBoard: { icon: MinusIcon, color: "red" },
  createComment: { icon: SendIcon, color: "blue" },
  changedLane: { icon: ArrowRightIcon, color: "blue" },
  assignedUser: { icon: UserPlusIcon, color: "green" },
  assignedExternalUser: { icon: UserPlusIcon, color: "green" },
  unassignedUser: { icon: UserXIcon, color: "red" },
  changedDeadline: { icon: CalendarIcon, color: "blue" },
  changedReviewDate: { icon: CalendarIcon, color: "blue" },
  ideaAssessorAssigned: { icon: PercentIcon, color: "blue" },
  assessorAssigned: { icon: UserPlusIcon, color: "blue" },
  assessorUnassigned: { icon: UserXIcon, color: "red" },
  assessorInvited: { icon: MailIcon, color: "blue" },
  assessmentSubmitted: { icon: PercentIcon, color: "blue" },
  assessmentHasConflict: { icon: PercentIcon, color: "orange" },
  sentAssessmentFeedback: { icon: MailIcon, color: "blue" },
  ideaSubmitted: { icon: UploadIcon, color: "green" },
  ideaUnsubmitted: { icon: Undo2Icon, color: "orange" },
  labelsAdded: { icon: TagsIcon, color: "blue" },
  labelsRemoved: { icon: TagsIcon, color: "red" },
  isPinnedUpdated: { icon: PinIcon, color: "blue" },
  changedSubmissionDeadline: { icon: CalendarIcon, color: "blue" },
  sentAuthorEmail: { icon: MailIcon, color: "blue" },
  impactStatementCreated: { icon: ChartLineIcon, color: "green" },
  impactStatementDeleted: { icon: Trash2Icon, color: "red" },
  finalOutcomeSubmitted: { icon: ChartLineIcon, color: "green" },
};
